import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { Add, Edit, Delete, ContentCopy, Send, Close } from '@mui/icons-material';
import QuillEditor from '../Common/QuillEditor';
import adminService from '../../services/adminService';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { useConfirm } from '../Common/ConfirmDialog';

// Admin által szerkeszthető email-sablonok (backend: services/emailTemplateService.js).
// A szöveg a közös Wedigo keretbe (logós fejléc, lábléc) kerül; meghívóknál a "nem kérem"
// blokk automatikus. Egy levéltípus aktív sablonjai között véletlenszerűen oszlik el a küldés
// (A/B teszt); ha nincs aktív, a beépített sablon megy ki.

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};

// Kiinduló szövegek új sablonhoz (a mostani beépített levelek alapján)
const STARTERS = {
  PROVIDER_INVITE: {
    subject: 'MEGHÍVÓ – Elkészült a(z) {{businessName}} profilja a Wedigo-n',
    bodyHtml: '<h2>Elkészült a profilod a Wedigo-n!</h2><p>Kedves <strong>{{firstName}}</strong>!</p><blockquote>Elkészítettük a(z) <strong>{{businessName}}</strong> profilját a Wedigo esküvői piactéren.</blockquote><p>Már csak rajtad múlik: állítsd be a jelszavad, fogadd el a partneri ÁSZF-et, és nézd át a profilod. Ha mindent rendben találsz (képek, árak, leírás), egy kattintással beküldheted jóváhagyásra.</p><p>Üdvözlettel,<br><strong>A Wedigo csapata</strong></p>',
    ctaLabel: 'Profil átnézése és jóváhagyása',
  },
  PROVIDER_INVITE_IMPORTED: {
    subject: 'MEGHÍVÓ – Előkészítettük a(z) {{businessName}} profilját a Wedigo-n',
    bodyHtml: '<h2>Előkészítettük a profilod a Wedigo-n!</h2><p>Kedves <strong>{{businessName}}</strong> csapata!</p><blockquote>A Wedigo egy új magyar esküvői piactér, ahol a jegyespárok minden szolgáltatót egy helyen keresnek. A vállalkozásotok nyilvánosan elérhető adatai alapján előkészítettük a(z) <strong>{{businessName}}</strong> profilját.</blockquote><p>A profil jelenleg <strong>nem nyilvános</strong> — csak akkor jelenik meg, ha ti magatok jóváhagyjátok, és a csapatunk is ellenőrizte.</p><p>Üdvözlettel,<br><strong>A Wedigo csapata</strong></p>',
    ctaLabel: 'Profil átnézése és jóváhagyása',
  },
  PROVIDER_INVITE_REMINDER: {
    subject: 'Emlékeztető: MEGHÍVÓ – a(z) {{businessName}} profilja még vár rád a Wedigo-n',
    bodyHtml: '<h2>Még vár rád a Wedigo-profilod</h2><p>Kedves <strong>{{businessName}}</strong> csapata!</p><p>{{daysAgo}} napja küldtünk meghívót a Wedigo esküvői piactérre. A profil még nem nyilvános — néhány perc az egész: állítsd be a jelszavad, egészítsd ki az adatokat, és küldd be jóváhagyásra.</p><p>Üdvözlettel,<br><strong>A Wedigo csapata</strong></p>',
    ctaLabel: 'Profil átnézése',
  },
  BROADCAST: {
    subject: 'Hírek a Wedigo-tól',
    bodyHtml: '<h2>Kedves {{contactName}}!</h2><p>Ide írd a levél szövegét.</p><p>Üdvözlettel,<br><strong>A Wedigo csapata</strong></p>',
    ctaLabel: 'Részletek megtekintése',
  },
};

const emptyTemplate = (usage) => ({
  usage,
  name: '',
  subject: STARTERS[usage]?.subject || '',
  bodyHtml: STARTERS[usage]?.bodyHtml || '',
  ctaLabel: STARTERS[usage]?.ctaLabel || '',
  ctaUrl: '',
  isActive: false,
});

const percent = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)}%` : '—');

// ---------------------------------------------------------------------------

function TemplateEditor({ open, initial, usages, onClose, onSaved }) {
  const isPhone = useMediaQuery('(max-width:899px)');
  const editorRef = useRef(null);
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { setForm(initial); setError(''); setTestResult(null); }, [initial]);

  const usage = usages.find(u => u.key === form?.usage);
  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  // Élő előnézet (debounce-olva, mentés nélkül)
  useEffect(() => {
    if (!open || !form) return undefined;
    const timer = setTimeout(async () => {
      try {
        setPreview(await adminService.previewEmailTemplate({ ...form, name: form.name || 'előnézet' }));
        setPreviewError('');
      } catch (err) {
        setPreviewError(err?.message || 'Az előnézet nem készíthető el.');
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [open, form]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await adminService.saveEmailTemplate(form);
      onSaved();
    } catch (err) {
      setError(err?.message || 'Mentés sikertelen.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const response = await adminService.sendTestEmailTemplate({ ...form, name: form.name || 'teszt' }, testTo);
      setTestResult({ severity: 'success', message: response?.message || 'Tesztlevél elküldve.' });
    } catch (err) {
      setTestResult({ severity: 'error', message: err?.message || 'Tesztlevél küldése sikertelen.' });
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isPhone} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {form.id ? 'Sablon szerkesztése' : 'Új email-sablon'}
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Bal: szerkesztő */}
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              select
              label="Levéltípus"
              value={form.usage}
              onChange={set('usage')}
              disabled={!!form.id}
              helperText={form.id ? 'Mentett sablon típusa nem változtatható.' : undefined}
            >
              {usages.map(u => <MenuItem key={u.key} value={u.key}>{u.label}</MenuItem>)}
            </TextField>
            <TextField label="Sablon neve (csak belső)" value={form.name} onChange={set('name')} placeholder="pl. Rövid, közvetlen hangnem" />
            <TextField label="Tárgy" value={form.subject} onChange={set('subject')} helperText="Változó itt is használható, pl. {{businessName}}" />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Változó beszúrása a szövegbe (a kurzor helyére):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(usage?.variables || []).map(v => (
                  <Tooltip key={v.name} title={`Minta: ${v.sample}`}>
                    <Chip size="small" label={v.label} onClick={() => editorRef.current?.insertText(`{{${v.name}}}`)} />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            <Box sx={{ '& .ql-editor': { minHeight: 260, fontSize: 15 }, '& .ql-container': { borderRadius: '0 0 4px 4px' } }}>
              <QuillEditor
                key={form.id ?? `new-${form.usage}`}
                ref={editorRef}
                value={form.bodyHtml}
                modules={QUILL_MODULES}
                onChange={(html) => setForm(prev => ({ ...prev, bodyHtml: html }))}
              />
            </Box>

            <Divider />
            <Typography variant="subtitle2">Gomb a szöveg alatt (opcionális)</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Gomb felirata" value={form.ctaLabel || ''} onChange={set('ctaLabel')} helperText="Üresen hagyva nincs gomb" />
              <TextField
                label="Gomb linkje"
                value={form.ctaUrl || ''}
                onChange={set('ctaUrl')}
                placeholder={usage?.defaultCtaUrl}
                helperText={`Üresen: ${usage?.defaultCtaUrl}`}
              />
            </Box>
            {usage?.includeDecline && (
              <Alert severity="info" variant="outlined">
                A „nem kérem, töröljétek az adatokat” blokk ennél a levéltípusnál automatikusan a levél aljára kerül.
              </Alert>
            )}
          </Stack>

          {/* Jobb: előnézet + teszt */}
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Előnézet (mintaadatokkal)</Typography>
            {previewError && <Alert severity="warning">{previewError}</Alert>}
            {preview && (
              <Typography variant="body2" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                <strong>Tárgy:</strong> {preview.subject}
              </Typography>
            )}
            <Paper variant="outlined" sx={{ height: { xs: 520, md: 640 }, overflow: 'hidden' }}>
              {preview
                ? <iframe title="Email előnézet" srcDoc={preview.html} style={{ width: '100%', height: '100%', border: 0 }} sandbox="" />
                : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress /></Box>}
            </Paper>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" fullWidth label="Tesztlevél címzettje" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              <Button variant="outlined" startIcon={<Send />} onClick={handleTest} disabled={!testTo} sx={{ whiteSpace: 'nowrap' }}>
                Teszt küldése
              </Button>
            </Box>
            {testResult && <Alert severity={testResult.severity}>{testResult.message}</Alert>}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Mégse</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Mentés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

export default function AdminEmailTemplatesPage() {
  const [confirm, confirmDialog] = useConfirm();
  const [usages, setUsages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await adminService.getEmailTemplates();
      setUsages(data.usages);
      setTemplates(data.templates);
    } catch (err) {
      setAlert({ severity: 'error', message: 'Hiba a sablonok betöltésekor.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (template) => {
    try {
      await adminService.setEmailTemplateActive(template.id, !template.isActive);
      load();
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Nem sikerült módosítani.' });
    }
  };

  const remove = async (template) => {
    if (!(await confirm({ title: 'Sablon törlése', message: `Törlöd a(z) „${template.name}” sablont? A statisztikája is elvész.`, confirmLabel: 'Törlés', color: 'error' }))) return;
    try {
      await adminService.deleteEmailTemplate(template.id);
      load();
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Törlés sikertelen.' });
    }
  };

  const duplicate = (template) => setEditing({
    usage: template.usage,
    name: `${template.name} (másolat)`,
    subject: template.subject,
    bodyHtml: template.bodyHtml,
    ctaLabel: template.ctaLabel || '',
    ctaUrl: template.ctaUrl || '',
    isActive: false,
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        A meghívók, az emlékeztető és a tömeges levelek szövege itt szerkeszthető, a megszokott Wedigo
        fejléccel és lábléccel. Ha egy levéltípusnál több sablon is aktív, a rendszer véletlenszerűen
        osztja el köztük a küldést (A/B teszt) — a statisztikából látszik, melyik működik jobban. Ha egy
        sem aktív, a beépített szöveg megy ki. A tömeges levél típusú sablonok a Tömeges email menüben választhatók.
      </Typography>

      {alert && <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>{alert.message}</Alert>}

      {usages.map(usage => {
        const list = templates.filter(t => t.usage === usage.key);
        const activeCount = list.filter(t => t.isActive).length;
        const isInvite = usage.key !== 'BROADCAST' && usage.key !== 'PROVIDER_INVITE_REMINDER';
        let status = 'Beépített szöveg megy ki';
        if (usage.key === 'BROADCAST') status = 'Küldéskor választható';
        else if (activeCount === 1) status = '1 aktív sablon';
        else if (activeCount > 1) status = `A/B teszt: ${activeCount} aktív sablon`;

        return (
          <Paper key={usage.key} variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', bgcolor: 'grey.50' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{usage.label}</Typography>
                <Chip size="small" sx={{ mt: 0.5 }} color={activeCount > 1 ? 'warning' : activeCount === 1 ? 'success' : 'default'} label={status} />
              </Box>
              <Button
                startIcon={<Add />}
                variant="contained"
                size="small"
                onClick={() => setEditing(emptyTemplate(usage.key))}
                sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } }}
              >
                Új sablon
              </Button>
            </Box>
            {list.length > 0 && (
              <ResponsiveTableContainer stickyLastColumn component="div">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Név / tárgy</TableCell>
                      {usage.key !== 'BROADCAST' && <TableCell align="center">Aktív</TableCell>}
                      <TableCell align="right">Kiküldve</TableCell>
                      {isInvite && <TableCell align="right">Beváltotta</TableCell>}
                      {isInvite && <TableCell align="right">Beküldte</TableCell>}
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map(t => (
                      <TableRow key={t.id} hover>
                        {/* maxWidth: 0 + szélesség: a hosszú tárgy levágódik (…), nem tolja ki a táblázatot */}
                        <TableCell sx={{ width: '45%', maxWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">{t.subject}</Typography>
                        </TableCell>
                        {usage.key !== 'BROADCAST' && (
                          <TableCell align="center"><Switch checked={t.isActive} onChange={() => toggleActive(t)} /></TableCell>
                        )}
                        <TableCell align="right">{t.stats.sent}</TableCell>
                        {isInvite && (
                          <TableCell align="right">
                            <Tooltip title={`${t.stats.claimed} / ${t.stats.invited} meghívott állított be jelszót`}>
                              <span>{percent(t.stats.claimed, t.stats.invited)}</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        {isInvite && (
                          <TableCell align="right">
                            <Tooltip title={`${t.stats.submitted} / ${t.stats.invited} meghívott küldte be a profilját`}>
                              <span>{percent(t.stats.submitted, t.stats.invited)}</span>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Szerkesztés"><IconButton size="small" onClick={() => setEditing(t)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Másolat"><IconButton size="small" onClick={() => duplicate(t)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Törlés"><IconButton size="small" color="error" onClick={() => remove(t)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>
            )}
          </Paper>
        );
      })}

      <TemplateEditor
        open={!!editing}
        initial={editing}
        usages={usages}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setAlert({ severity: 'success', message: 'Sablon elmentve.' }); load(); }}
      />
      {confirmDialog}
    </Box>
  );
}
